import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { DataTable } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Package, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { StockAudit, BookCopy, ColumnFilterOption } from "@/types";
import { formatGridDate } from "@/lib/utils";

export default function StockRegisterPage() {
  const { api, user } = useAuth();
  const [audits, setAudits] = useState<StockAudit[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin" || user?.role === "librarian";

  const [copySearch, setCopySearch] = useState("");
  const [copies, setCopies] = useState<BookCopy[]>([]);
  const [selectedCopy, setSelectedCopy] = useState<BookCopy | null>(null);
  const [auditStatus, setAuditStatus] = useState("verified");
  const [auditNotes, setAuditNotes] = useState("");

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAudit, setEditingAudit] = useState<StockAudit | null>(null);
  const [editForm, setEditForm] = useState({ status: "", notes: "" });

  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});
  const filterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchAudits = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 20 };
      const textParts = [columnFilters.book_title, columnFilters.accession_no].filter(Boolean);
      if (textParts.length) params.search = textParts.join(" ");
      if (columnFilters.status) params.status = columnFilters.status;
      const res = await api.get("/stock", { params });
      setAudits(res.data.items);
      setTotalPages(res.data.pages);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  }, [api, page, columnFilters]);

  useEffect(() => {
    fetchAudits();
  }, [fetchAudits]);

  const searchCopies = useCallback(
    async (q: string) => {
      if (q.length < 2) {
        setCopies([]);
        return;
      }
      try {
        const res = await api.get("/books", { params: { search: q, limit: 5 } });
        const allCopies: any[] = [];
        for (const b of res.data.items) {
          const c = await api.get(`/books/${b.id}/copies`);
          c.data.forEach((copy: any) => allCopies.push({ ...copy, book_title: b.title }));
        }
        setCopies(allCopies);
      } catch {}
    },
    [api],
  );

  const handleCopySearchChange = (v: string) => {
    setCopySearch(v);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => searchCopies(v), 300);
  };

  const handleColumnFiltersChange = useCallback((filters: Record<string, any>) => {
    setColumnFilters(filters);
    if (filterTimer.current) clearTimeout(filterTimer.current);
    filterTimer.current = setTimeout(() => setPage(1), 400);
  }, []);

  const submitAudit = async () => {
    if (!selectedCopy) {
      toast.error("Select a book copy");
      return;
    }
    try {
      await api.post("/stock", { book_copy_id: selectedCopy.id, status: auditStatus, notes: auditNotes });
      toast.success("Audit recorded");
      setSelectedCopy(null);
      setCopies([]);
      setCopySearch("");
      setAuditNotes("");
      setAuditStatus("verified");
      fetchAudits();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const openEditAudit = (audit: StockAudit) => {
    setEditingAudit(audit);
    setEditForm({ status: audit.status, notes: audit.notes || "" });
    setEditDialogOpen(true);
  };

  const saveEditAudit = async () => {
    if (!editingAudit) return;
    try {
      await api.put(`/stock/${editingAudit.id}`, editForm);
      toast.success("Audit updated");
      setEditDialogOpen(false);
      fetchAudits();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const deleteAudit = async (id: string) => {
    if (!window.confirm("Delete this audit entry?")) return;
    try {
      await api.delete(`/stock/${id}`);
      toast.success("Deleted");
      fetchAudits();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const statusOptions: ColumnFilterOption[] = [
    { label: "Verified", value: "verified" },
    { label: "Missing", value: "missing" },
    { label: "Damaged", value: "damaged" },
  ];

  const columns: any[] = [
    { accessorKey: "book_title", header: "Book", meta: { filterVariant: "text", filterPlaceholder: "Book..." } },
    {
      accessorKey: "accession_no",
      header: "Accession No",
      meta: { filterVariant: "text", filterPlaceholder: "Accession..." },
      cell: ({ row }: any) => <span className="font-mono text-xs">{row.original.accession_no}</span>,
    },
    {
      accessorKey: "status",
      header: "Status",
      meta: { filterVariant: "select", filterOptions: statusOptions, filterPlaceholder: "Status" },
      cell: ({ row }: any) => {
        const s = row.original.status;
        const cls = s === "verified" ? "bg-green-100 text-green-800" : s === "missing" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800";
        return <Badge className={`${cls} hover:${cls}`}>{s}</Badge>;
      },
    },
    { accessorKey: "notes", header: "Notes" },
    {
      accessorKey: "audit_date",
      header: "Date",
      meta: { filterVariant: "date-range" },
      cell: ({ row }: any) => formatGridDate(row.original.audit_date || row.original.created_at),
    },
    ...(isAdmin
      ? [
          {
            id: "actions",
            header: "Actions",
            cell: ({ row }: any) => (
              <div className="flex items-center gap-1" onClick={(e: any) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditAudit(row.original)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteAudit(row.original.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-6" data-testid="stock-register-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          Stock Register
        </h1>
      </div>

      {isAdmin && (
        <Card className="border-0 shadow-sm" data-testid="stock-audit-form">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5 text-primary" /> New Audit Entry
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Input
                value={copySearch}
                onChange={(e) => handleCopySearchChange(e.target.value)}
                placeholder="Search book to audit (type 2+ letters)"
                data-testid="stock-search-input"
              />
            </div>
            {copies.length > 0 && !selectedCopy && (
              <div className="border rounded-md bg-card max-h-40 overflow-y-auto">
                {copies.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => {
                      setSelectedCopy(c);
                      setCopies([]);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-muted text-sm border-b last:border-0"
                  >
                    <span className="font-medium">{c.book_title}</span>
                    <span className="font-mono text-xs ml-2">{c.accession_no}</span>
                    <Badge variant="outline" className="ml-2">
                      {c.status}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
            {selectedCopy && (
              <div className="p-2 bg-primary/5 rounded-md flex items-center gap-2">
                <Badge className="bg-primary text-white hover:bg-primary">{selectedCopy.book_title}</Badge>
                <span className="font-mono text-xs">{selectedCopy.accession_no}</span>
                <Button variant="ghost" size="sm" onClick={() => setSelectedCopy(null)} className="ml-auto h-7 text-xs">
                  Change
                </Button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={auditStatus} onValueChange={setAuditStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="verified">Verified</SelectItem>
                    <SelectItem value="missing">Missing</SelectItem>
                    <SelectItem value="damaged">Damaged</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Notes</Label>
                <Textarea value={auditNotes} onChange={(e) => setAuditNotes(e.target.value)} rows={1} />
              </div>
            </div>
            <Button className="bg-primary hover:bg-primary/90" onClick={submitAudit} disabled={!selectedCopy} data-testid="stock-submit-btn">
              Record Audit
            </Button>
          </CardContent>
        </Card>
      )}

      <DataTable
        columns={columns}
        data={audits}
        totalPages={totalPages}
        currentPage={page}
        onPageChange={setPage}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        loading={loading}
        onRowClick={isAdmin ? openEditAudit : undefined}
        data-testid="stock-table"
      />

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Audit Entry</DialogTitle>
          </DialogHeader>
          {editingAudit && (
            <div className="space-y-4">
              <div className="p-2 bg-muted rounded text-sm">
                <span className="font-medium">{editingAudit.book_title}</span> —{" "}
                <span className="font-mono text-xs">{editingAudit.accession_no}</span>
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="verified">Verified</SelectItem>
                    <SelectItem value="missing">Missing</SelectItem>
                    <SelectItem value="damaged">Damaged</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Notes</Label>
                <Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={2} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="bg-primary hover:bg-primary/90" onClick={saveEditAudit}>
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
